import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Crown, Pencil, Trash2, Upload, Undo2, History } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { MARKET_LABELS } from "@/lib/api";
import { fmtEvent } from "@/lib/time";
import { publishPick, unpublishPick, deletePick, settlePick, correctPick, apiError } from "@/lib/adminApi";
import { GradeSuggestion } from "@/pages/admin/GradeSuggestion";

const ADMIN_TZ = "Australia/Perth";
const SETTLE = [
  { v: "win", label: "Win", cls: "border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10" },
  { v: "loss", label: "Loss", cls: "border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10" },
  { v: "push", label: "Push", cls: "border-slate-500/50 text-slate-300 hover:bg-slate-500/10" },
  { v: "void", label: "Void", cls: "border-slate-500/50 text-slate-400 hover:bg-slate-500/10" },
];

function CorrectionForm({ pick, onDone }) {
  const [status, setStatus] = useState(SETTLE.find((s) => s.v !== pick.status).v);
  const [reason, setReason] = useState("");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => correctPick(pick.id, status, reason),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Result corrected — audit trail recorded"); onDone(); },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="mt-3 rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3" data-testid={`correct-form-${pick.id}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">Correct settled result (audited)</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {SETTLE.filter((s) => s.v !== pick.status).map((s) => (
          <button key={s.v} data-testid={`correct-status-${s.v}-${pick.id}`} onClick={() => setStatus(s.v)} className={`rounded-md border px-3 py-1 font-display text-xs font-bold uppercase ${s.cls} ${status === s.v ? "bg-white/5" : ""}`}>{s.label}</button>
        ))}
      </div>
      <input data-testid={`correct-reason-${pick.id}`} className="admin-input mt-2" placeholder="Reason for correction (required, shown in audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="mt-2 flex gap-2">
        <button data-testid={`correct-submit-${pick.id}`} disabled={reason.trim().length < 3 || mut.isPending} onClick={() => mut.mutate()} className="btn-primary !px-4 !py-1.5 text-xs disabled:opacity-50">Apply Correction</button>
        <button onClick={onDone} className="rounded-md border border-[#2A364F] px-3 py-1.5 font-mono text-[11px] uppercase text-slate-400">Cancel</button>
      </div>
    </div>
  );
}

export function AdminPickRow({ pick, compact = false }) {
  const qc = useQueryClient();
  const [correcting, setCorrecting] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const refresh = () => qc.invalidateQueries();

  const publish = useMutation({ mutationFn: () => publishPick(pick.id), onSuccess: () => { refresh(); toast.success("Pick published — now live on the Picks page"); }, onError: (e) => toast.error(apiError(e)) });
  const unpublish = useMutation({ mutationFn: () => unpublishPick(pick.id), onSuccess: () => { refresh(); toast.success("Pick moved back to draft"); }, onError: (e) => toast.error(apiError(e)) });
  const remove = useMutation({ mutationFn: () => deletePick(pick.id), onSuccess: () => { refresh(); toast.success("Draft deleted"); }, onError: (e) => toast.error(apiError(e)) });
  const [legResults, setLegResults] = useState(() => (pick.legs || []).map(() => "pending"));
  const settle = useMutation({ mutationFn: ({ s, evidence }) => settlePick(pick.id, s, pick.pick_type === "multi" ? legResults : undefined, evidence), onSuccess: (p) => { refresh(); toast.success(`Settled as ${p.status.toUpperCase()} · ${p.profit > 0 ? "+" : ""}${p.profit}U`); }, onError: (e) => toast.error(apiError(e)) });

  const winProfit = useMemo(() => (pick.units * (pick.odds - 1)).toFixed(2), [pick.units, pick.odds]);
  const isDraft = pick.publish_status === "draft";
  const isPending = pick.status === "pending";
  const title = pick.player ? `${pick.player} — ${pick.selection}` : pick.selection;

  return (
    <div className="rounded-lg border border-[#1E2738] bg-[#0F131D] p-4" data-testid={`admin-pick-row-${pick.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="tag border border-[#2A364F] text-slate-300">{pick.league} · {MARKET_LABELS[pick.market] || pick.market}</span>
            {pick.visibility === "vip" ? <span className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"><Crown size={10} /> VIP</span> : <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">Free</span>}
            {pick.best_bet && <span className="tag border border-[#00E676]/40 text-[#00E676]">Best Bet</span>}
            <span className={`tag border ${isDraft ? "border-slate-500/40 text-slate-400" : "border-[#00E676]/40 text-[#00E676]"}`} data-testid={`publish-status-${pick.id}`}>{isDraft ? "Draft" : "Published"}</span>
            {pick.corrected && <span className="tag border border-[#F59E0B]/40 text-[#F59E0B]" data-testid={`corrected-tag-${pick.id}`}>Corrected</span>}
            <StatusBadge status={pick.status} profit={pick.profit} />
          </div>
          <p className="mt-2 truncate font-display text-base font-bold uppercase tracking-wide text-white" data-testid={`admin-pick-title-${pick.id}`}>{title}</p>
          <p className="font-mono text-[11px] text-slate-500">{pick.game} · {pick.odds.toFixed(2)} · {pick.units}U · {pick.event_start_utc ? `${fmtEvent(pick.event_start_utc, ADMIN_TZ)} AWST` : dayjs(pick.posted_at).format("MMM D")}
            {pick.pick_type === "multi" && ` · ${pick.legs?.length || 0} legs`}
            {" · "}<span className={pick.fixture_verified ? "text-[#00E676]" : "text-[#F59E0B]"} data-testid={`fixture-flag-${pick.id}`}>{pick.fixture_verified ? "Fixture verified" : pick.manual_confirmed ? "Manual fixture (confirmed)" : "Fixture unverified"}</span></p>
        </div>

        {!compact && (
          <div className="flex flex-wrap items-center gap-2">
            {isPending && (
              <>
                <Link to={`/admin/picks/${pick.id}/edit`} data-testid={`edit-pick-${pick.id}`} className="rounded-md border border-[#2A364F] p-2 text-slate-400 hover:text-white" title="Edit"><Pencil size={14} /></Link>
                {isDraft ? (
                  <>
                    <button data-testid={`publish-pick-${pick.id}`} onClick={() => publish.mutate()} className="btn-primary !px-3 !py-1.5 text-xs"><Upload size={13} /> Publish</button>
                    <button data-testid={`delete-pick-${pick.id}`} onClick={() => window.confirm("Delete this draft?") && remove.mutate()} className="rounded-md border border-[#EF4444]/40 p-2 text-[#EF4444] hover:bg-[#EF4444]/10" title="Delete draft"><Trash2 size={14} /></button>
                  </>
                ) : (
                  <button data-testid={`unpublish-pick-${pick.id}`} onClick={() => unpublish.mutate()} className="rounded-md border border-[#2A364F] px-3 py-1.5 font-mono text-[11px] uppercase text-slate-400 hover:text-white"><Undo2 size={12} className="inline" /> Unpublish</button>
                )}
              </>
            )}
            {!isPending && (
              <>
                {pick.audit?.length > 0 && <button data-testid={`audit-toggle-${pick.id}`} onClick={() => setShowAudit((v) => !v)} className="rounded-md border border-[#2A364F] px-3 py-1.5 font-mono text-[11px] uppercase text-slate-400 hover:text-white"><History size={12} className="inline" /> Audit ({pick.audit.length})</button>}
                <button data-testid={`correct-pick-${pick.id}`} onClick={() => setCorrecting((v) => !v)} className="rounded-md border border-[#F59E0B]/40 px-3 py-1.5 font-mono text-[11px] uppercase text-[#F59E0B] hover:bg-[#F59E0B]/10">Correct Result</button>
              </>
            )}
          </div>
        )}
      </div>

      {!compact && isPending && !isDraft && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#1E2738] pt-3" data-testid={`settle-controls-${pick.id}`}>
          <GradeSuggestion pick={pick} applying={settle.isPending} onApply={(s, evidence) => settle.mutate({ s, evidence })} />
          {pick.pick_type === "multi" && (
            <div className="mb-2 flex w-full flex-wrap gap-2" data-testid={`leg-results-${pick.id}`}>
              {(pick.legs || []).map((l, i) => (
                <label key={`${i}-${l.selection}`} className="flex items-center gap-1 font-mono text-[10px] uppercase text-slate-400">Leg {i + 1}
                  <select data-testid={`leg-result-${i}-${pick.id}`} value={legResults[i]} onChange={(e) => setLegResults((r) => r.map((v, j) => (j === i ? e.target.value : v)))} className="rounded border border-[#2A364F] bg-[#0A0D14] px-1 py-0.5 text-[10px] text-slate-200">
                    {["pending", "win", "loss", "void"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              ))}
            </div>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Settle pick:</span>
          {SETTLE.map((s) => (
            <button key={s.v} data-testid={`settle-${s.v}-${pick.id}`} disabled={settle.isPending} onClick={() => settle.mutate({ s: s.v })} className={`rounded-md border px-3 py-1 font-display text-xs font-bold uppercase transition-colors ${s.cls}`}>{s.label}</button>
          ))}
          <span className="ml-auto font-mono text-[10px] text-slate-500">Win = +{winProfit}U · Loss = -{pick.units.toFixed(2)}U · Push = 0U</span>
        </div>
      )}
      {!compact && isPending && isDraft && <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-600">Publish this pick before settling it.</p>}

      {correcting && <CorrectionForm pick={pick} onDone={() => setCorrecting(false)} />}
      {showAudit && (
        <div className="mt-3 space-y-1.5 rounded-lg border border-[#1E2738] p-3" data-testid={`audit-list-${pick.id}`}>
          {pick.audit.map((a) => (
            <p key={a.at} className="font-mono text-[11px] text-slate-400">
              {dayjs(a.at).format("MMM D, YYYY HH:mm")} · {a.by} changed <b className="text-white">{a.from_status.toUpperCase()}</b> ({a.from_profit > 0 ? "+" : ""}{a.from_profit}U) → <b className="text-white">{a.to_status.toUpperCase()}</b> ({a.to_profit > 0 ? "+" : ""}{a.to_profit}U) — "{a.reason}"
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
